











































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import ApiConfiguration from '@/classes/ApiConfiguration';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import { ConfigurationChangeModel, StoreConstants } from '@/types';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import MStoreTable from '@/components/shared/Table/MStoreTable.vue';
import MStoreMakeChangesLock from '@/components/shared/MStoreMakeChangesLock.vue';
import SweepFileFolderSourceModal from '@/components/configuration/sweep/SweepFileFolderSourceModal.vue';
import SweepFolderDefaultsModal from '@/components/configuration/sweep/SweepFolderDefaultsModal.vue';
import { ModalHelpers } from '@/mixins/modal';
import { BvTableFieldArray } from 'bootstrap-vue';
import { SweepFileSourceModel, SweepPostProcessAction } from '@/services/GeneratedApi';

const EMPTY_SOURCE = (): SweepFileSourceModel => ({
  id: 0,
  description: '',
  sourceFolderPath: '',
  includeSubFolders: false,
  destinationCabinetId: null,
  destinationDocumentTypeId: undefined,
  useDefaultIgnoreFileTypes: true,
  ignoreFileExtensions: null,
  ignoreFileNameRegex: null,
  pluginId: null,
  postProcessAction: SweepPostProcessAction.Archive,
  archiveFolderPath: null,
  failedFolderPath: '',
  enabled: true,
  createdDate: undefined,
  modifiedDate: undefined
});

export default Vue.extend({
  name: 'SweepFileFolderSourceConfiguration',
  mixins: [ModalHelpers],
  components: {
    LoadingSpinner,
    MStoreTable,
    MStoreMakeChangesLock,
    SweepFileFolderSourceModal,
    SweepFolderDefaultsModal
  },
  data() {
    return {
      isPageLoading: true,
      isLoading: false,
      searchQuery: '',
      sources: [] as SweepFileSourceModel[],
      showCreateModal: false,
      showEditModal: false,
      showGlobalSettingsModal: false,
      editingSource: null as SweepFileSourceModel | null
    };
  },
  computed: {
    ...mapGetters(StoreConstants.ConfigurationChange.ModuleName, [
      StoreConstants.ConfigurationChange.Getters.ConfigurationChangeLock,
      StoreConstants.ConfigurationChange.Getters.BypassConfigurationChangeLock,
      StoreConstants.ConfigurationChange.Getters.ConfigurationChangeLockedByCurrentSession,
      StoreConstants.ConfigurationChange.Getters.ConfigurationChangeIsLoading
    ]),
    lock(this: any) {
      return this[StoreConstants.ConfigurationChange.Getters.ConfigurationChangeLock];
    },
    bypassLockingOnToolkit(this: any) {
      return this[StoreConstants.ConfigurationChange.Getters.BypassConfigurationChangeLock];
    },
    isEditMode(this: any) {
      return this[StoreConstants.ConfigurationChange.Getters.ConfigurationChangeLockedByCurrentSession];
    },
    isLockLoading(this: any) {
      return this[StoreConstants.ConfigurationChange.Getters.ConfigurationChangeIsLoading];
    },
    headerText(this: any): string {
      return `File Folder Sweep Sources`;
    }
  },
  methods: {
    ...mapActions(StoreConstants.ConfigurationChange.ModuleName, [
      StoreConstants.ConfigurationChange.Actions.BeginChange,
      StoreConstants.ConfigurationChange.Actions.EndChange,
      StoreConstants.ConfigurationChange.Actions.GetChangeWithErrorPage
    ]),
    ...mapActions('applicationDirtyStore', ['setApplicationDirty']),
    async onChangeReasonEntered(this: any, changeReason: string) {
      try {
        await this[StoreConstants.ConfigurationChange.Actions.BeginChange]({
          controllerPath: this.CHANGE_CONTROLLER_PATH,
          configurationChange: {
            description: changeReason
          } as ConfigurationChangeModel
        });
      } catch (error) {
        this.$log(error);
      }
    },
    async onReleaseLock(this: any) {
      await this[StoreConstants.ConfigurationChange.Actions.EndChange](this.CHANGE_CONTROLLER_PATH);
    },
    async loadSources(this: any) {
      this.isLoading = true;
      try {
        const response = await ApiConfiguration.apiWithErrorPage().sweepFileSourceConfiguration.sweepFileSourceConfigurationList();
        this.sources = response || [];
      } catch (error) {
        this.$log(error);
        SnotifyConfiguration.showError('Failed to load sweep file sources');
      } finally {
        this.isLoading = false;
      }
    },
    openGlobalSettingsModal(this: any) {
      this.showGlobalSettingsModal = true;
    },
    onGlobalSettingsSaved(this: any) {
      this.showGlobalSettingsModal = false;
      SnotifyConfiguration.showSuccess('Sweep settings saved');
    },
    openCreateModal(this: any) {
      this.editingSource = EMPTY_SOURCE();
      this.showCreateModal = true;
      this.showEditModal = false;
    },
    openEditModal(this: any, source: SweepFileSourceModel) {
      this.editingSource = { ...source };
      this.showEditModal = true;
      this.showCreateModal = false;
    },
    closeModal(this: any) {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingSource = null;
    },
    async onSourceSaved(this: any) {
      this.closeModal();
      this.setApplicationDirty(false);
      await this.loadSources();
      SnotifyConfiguration.showSuccess('Sweep file source saved successfully');
    },
    async onDeleteSource(this: any, source: SweepFileSourceModel) {
      const confirmed = await this.showConfirmationModalAsync(this.$bvModal, `Are you sure you want to delete "${source.description}"?`);
      if (!confirmed) {
        return;
      }

      try {
        this.isLoading = true;
        await ApiConfiguration.apiWithErrorPage().sweepFileSourceConfiguration.sweepFileSourceConfigurationDelete(source.id!);
        await this.loadSources();
        SnotifyConfiguration.showSuccess('Sweep file source deleted');
      } catch (error) {
        this.$log(error);
        SnotifyConfiguration.showError('Failed to delete sweep file source');
      } finally {
        this.isLoading = false;
      }
    },
    getPostProcessDisplayName(action: SweepPostProcessAction): string {
      switch (action) {
        case SweepPostProcessAction.Archive:
          return 'Move to Archive';
        case SweepPostProcessAction.Delete:
          return 'Delete Source Files';
        default:
          return 'Unknown';
      }
    },
    onSearchQueryUpdate(this: any, key: string, value: string) {
      this.searchQuery = value;
    }
  },
  async mounted(this: any) {
    this[StoreConstants.ConfigurationChange.Actions.GetChangeWithErrorPage](this.CHANGE_CONTROLLER_PATH);
    try {
      await this.loadSources();
    } finally {
      this.isPageLoading = false;
    }
  },
  created(this: any) {
    this.CHANGE_CONTROLLER_PATH = 'sweepFileSourceConfiguration';
    this.TABLE_FIELDS = [
      { key: 'actions', label: '', class: 'action-element' },
      { key: 'enabled', label: 'Status', sortable: true, class: 'status-column', formatter: (value: any, key: string, item: any) => (item.enabled ? 'Enabled' : 'Disabled') },
      { key: 'description', label: 'Description', sortable: true },
      { key: 'sourceFolderPath', label: 'Source Path', sortable: true },
      { key: 'destinationCabinetName', label: 'Target Cabinet', sortable: true },
      { key: 'destinationDocumentTypeName', label: 'Target Document Type', sortable: true },
      { key: 'failedFolderPath', label: 'Failed Folder', sortable: true },
      { key: 'archiveFolderPath', label: 'Archive Folder', sortable: true },
      {
        key: 'postProcessAction',
        label: 'Post-Process Action',
        sortable: true,
        formatter: (value: SweepPostProcessAction) => {
          switch (value) {
            case SweepPostProcessAction.Archive:
              return 'Move to Archive';
            case SweepPostProcessAction.Delete:
              return 'Delete Source Files';
            default:
              return 'Unknown';
          }
        }
      }
    ] as BvTableFieldArray;
  }
});
